import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, SearchX, X } from 'lucide-react';
import Seo from '../components/Seo';
import ResourceCard from '../components/resources/ResourceCard';
import { RESOURCE_TYPES } from '../lib/resources';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 12;
const SORTS = [
  { key: 'featured', label: 'Recommended' },
  { key: 'newest', label: 'Newest' },
  { key: 'az', label: 'A–Z' },
];

const chipClass = (active) =>
  `inline-flex flex-none items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
    active
      ? 'border-[#0a1f2e] bg-[#0a1f2e] text-white'
      : 'border-gray-200 bg-white text-gray-700 hover:border-[#0a1f2e]/40 hover:text-[#0a1f2e]'
  }`;

const Count = ({ n, active }) => (
  <span className={`text-xs tabular-nums ${active ? 'text-[#c8ff00]' : 'text-gray-400'}`}>{n}</span>
);

const SkeletonCard = () => (
  <div className="h-56 animate-pulse rounded-2xl border border-gray-100 bg-white p-5">
    <div className="mb-4 flex items-center gap-2.5"><div className="h-9 w-9 rounded-xl bg-gray-100" /><div className="h-3 w-20 rounded bg-gray-100" /></div>
    <div className="h-4 w-3/4 rounded bg-gray-100" />
    <div className="mt-3 h-3 w-full rounded bg-gray-100" />
    <div className="mt-2 h-3 w-2/3 rounded bg-gray-100" />
  </div>
);

const Resources = () => {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const type = params.get('type') || '';
  const product = params.get('product') || '';
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'featured';
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

  const [query, setQuery] = useState(q);
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const resultsRef = useRef(null);
  const filtering = Boolean(type || product || q);

  // Old links: /resources#user-guides -> the hub filtered to user guides
  useEffect(() => {
    if (location.hash === '#user-guides') navigate('/resources?type=user_guide', { replace: true });
  }, [location.hash, navigate]);

  useEffect(() => { setQuery(q); }, [q]);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (resetPage) next.delete('page');
    if (next.get('sort') === 'featured') next.delete('sort');
    setParams(next, { replace: 'q' in changes && Object.keys(changes).length === 1 });
  };

  // Search as you type (debounced) - the query string keeps the result shareable.
  useEffect(() => {
    if (query === q) return undefined;
    const t = setTimeout(() => update({ q: query.trim() }), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/resources/featured/list?limit=8`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setFeatured)
      .catch(() => setFeatured([]));
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    const qs = new URLSearchParams({ sort, page: String(page), page_size: String(PAGE_SIZE) });
    if (type) qs.set('type', type);
    if (product) qs.set('product', product);
    if (q) qs.set('q', q);
    if (!filtering) qs.set('exclude_featured', 'true');
    setLoading(true);
    setError(false);
    fetch(`${BACKEND_URL}/api/resources?${qs}`, { signal: ctrl.signal })
      .then((r) => { if (!r.ok) throw new Error('load'); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setError(true); setLoading(false); } });
    return () => ctrl.abort();
  }, [type, product, q, sort, page, filtering]);

  const facets = data?.facets;
  const typeChips = facets?.types || [];
  const productChips = facets?.products || [];
  const allCount = useMemo(() => typeChips.reduce((n, t) => n + t.count, 0), [typeChips]);
  const items = data?.items || [];
  // Without filters the featured row shows the featured items and the grid shows everything else.
  const hasFeatured = !filtering && featured.length > 0;
  const showFeatured = hasFeatured && page === 1;
  const heading = type ? (RESOURCE_TYPES[type]?.plural || 'Resources') : q ? 'Search results' : hasFeatured ? 'More resources' : 'All resources';

  const goPage = (p) => {
    update({ page: p > 1 ? String(p) : '' }, { resetPage: false });
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const clearAll = () => { setQuery(''); setParams(new URLSearchParams()); };

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo
        title={type && RESOURCE_TYPES[type] ? `${RESOURCE_TYPES[type].plural} – Resources` : 'Resources: Guides, How-tos & Datasheets'}
        path="/resources"
        breadcrumbs={[{ name: 'Resources', path: '/resources' }]}
        description="VOIP2 resources: user guides, quick references, how-to articles, videos and datasheets for the Phone System, SIP Trunk, phone numbers and apps."
      />

      {/* Hero + search */}
      <section className="bg-[#0a1f2e] px-4 pb-14 pt-28 text-white sm:px-6 lg:px-8 lg:pt-32">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#c8ff00]">Resources</p>
          <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl">
            Guides, how-tos and answers
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
            Everything you need to set up and get the most from VOIP2.
          </p>
          <form
            role="search"
            className="relative mx-auto mt-8 max-w-2xl"
            onSubmit={(e) => { e.preventDefault(); update({ q: query.trim() }); }}
          >
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides, e.g. voicemail, forwarding, Teams"
              aria-label="Search resources"
              maxLength={120}
              className="h-14 w-full rounded-full border-0 bg-white pl-14 pr-12 text-base text-[#0a1f2e] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)] outline-none ring-[#c8ff00] placeholder:text-gray-400 focus:ring-4 [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); update({ q: '' }); }} aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-[#0a1f2e]">
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
        </div>
      </section>

      {/* Featured */}
      {showFeatured && (
        <section id="user-guides" className="scroll-mt-28 px-4 pt-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-5 flex items-baseline justify-between gap-4">
              <h2 className="font-display text-2xl font-semibold">Start here</h2>
              <Link to="/resources?type=user_guide" className="text-sm font-medium text-gray-500 hover:text-[#0a1f2e]">All user guides</Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((r) => <ResourceCard key={r.id} r={r} featured />)}
            </div>
          </div>
        </section>
      )}

      {/* Filters + results */}
      <section ref={resultsRef} className="scroll-mt-24 px-4 pb-24 pt-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex flex-col gap-4 border-b border-gray-100 pb-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="font-display text-2xl font-semibold">{heading}</h2>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {data && <span className="tabular-nums" aria-live="polite">{data.total} {data.total === 1 ? 'result' : 'results'}</span>}
                <label className="sr-only" htmlFor="resource-sort">Sort</label>
                <select id="resource-sort" value={sort} onChange={(e) => update({ sort: e.target.value })}
                  className="rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-[#0a1f2e] outline-none focus:border-[#0a1f2e]">
                  {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {typeChips.length > 0 && (
              <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" role="group" aria-label="Filter by type">
                <div className="flex gap-2 sm:flex-wrap">
                  <button type="button" className={chipClass(!type)} onClick={() => update({ type: '' })} aria-pressed={!type}>
                    All <Count n={allCount} active={!type} />
                  </button>
                  {typeChips.map((t) => (
                    <button key={t.key} type="button" className={chipClass(type === t.key)} aria-pressed={type === t.key}
                      onClick={() => update({ type: type === t.key ? '' : t.key })}>
                      {RESOURCE_TYPES[t.key]?.plural || t.label} <Count n={t.count} active={type === t.key} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {productChips.length > 1 && (
              <div className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0" role="group" aria-label="Filter by product">
                <span className="flex-none text-xs font-semibold uppercase tracking-wider text-gray-400">Product</span>
                {productChips.map((p) => (
                  <button key={p.key} type="button" aria-pressed={product === p.key}
                    onClick={() => update({ product: product === p.key ? '' : p.key })}
                    className={`flex-none rounded-full px-3 py-1 text-[13px] font-medium transition-colors ${
                      product === p.key ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'bg-[#f5f7f9] text-gray-600 hover:text-[#0a1f2e]'}`}>
                    {p.label}
                  </button>
                ))}
                {product && !productChips.some((p) => p.key === product) && (
                  <button type="button" onClick={() => update({ product: '' })} className="rounded-full bg-[#c8ff00] px-3 py-1 text-[13px] font-medium">
                    Clear product
                  </button>
                )}
              </div>
            )}
          </div>

          {error ? (
            <div className="rounded-2xl border border-gray-200 px-6 py-16 text-center">
              <p className="font-display text-lg font-semibold">Resources could not be loaded</p>
              <p className="mt-2 text-sm text-gray-500">Please refresh the page, or try again in a moment.</p>
            </div>
          ) : loading && !data ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : items.length === 0 && hasFeatured ? null : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 px-6 py-16 text-center">
              <SearchX className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
              <p className="mt-4 font-display text-lg font-semibold">
                {filtering ? 'Nothing matches that yet' : 'More resources are on the way'}
              </p>
              <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
                {filtering ? 'Try another word or clear the filters. ' : ''}
                Can't find what you need? <Link to="/contact" className="font-medium text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-2">Ask our team</Link>.
              </p>
              {filtering && (
                <button type="button" onClick={clearAll} className="mt-6 rounded-full bg-[#c8ff00] px-5 py-2.5 text-sm font-semibold text-[#0a1f2e] hover:brightness-95">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-5 transition-opacity sm:grid-cols-2 lg:grid-cols-3 ${loading ? 'opacity-60' : ''}`}>
              {items.map((r) => <ResourceCard key={r.id} r={r} />)}
            </div>
          )}

          {data && data.pages > 1 && (
            <nav className="mt-10 flex items-center justify-center gap-3" aria-label="Pages">
              <button type="button" disabled={page <= 1} onClick={() => goPage(page - 1)}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium disabled:opacity-40 hover:enabled:border-[#0a1f2e]">
                <ArrowLeft className="h-4 w-4" /> Previous
              </button>
              <span className="text-sm tabular-nums text-gray-500">Page {page} of {data.pages}</span>
              <button type="button" disabled={page >= data.pages} onClick={() => goPage(page + 1)}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium disabled:opacity-40 hover:enabled:border-[#0a1f2e]">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            </nav>
          )}
        </div>
      </section>
    </div>
  );
};

export default Resources;
